import * as swcHelpers from '@swc/helpers';
import { createSelector } from 'reselect';
import { CommerceToolsApi } from '@grandvisionhq/commercetools';
import { Selectors } from '@grandvisionhq/configurator';
import { statusSuccess } from '@grandvisionhq/state';
import { getLocalizedValue, isDefined } from '@grandvisionhq/utils';
import { getBundleProductTitle } from '../../utils/get-bundle-product-title.js';
import { getLensProductVariantName } from '../../utils/get-lens-product-variant-name.js';
import { isNewPrescriptionAndUpload } from '../../utils/is-new-prescription-upload.js';
import { convertToLensTypeInfo, convertToLensAddOnInfo } from '../helpers.js';
import { getLensTypes, lookupParentLensType, getPackages, lookupParentAddOn } from '../products/selectors.internal.js';
import '../add-to-cart/selectors.js';
import '../bundles/selectors.js';
import '../bundles/reducer.js';
import { getBundlePrice, getPrescription, getSelectedLensType, getSelectedPackage, getSelectedAddOn, hasVisitedStepAddOn, getPrescriptionType, hasVisitedStepPrescription } from '../configurator/selectors.js';
import '../configurator/reducer.js';
import '../products/selectors.js';
import '../products/reducer.js';
import { configuratorIdProp, stateProp } from '../selectors.internal.js';

const stepMapping = {
    lensType: {
        name: 'lens type',
        id: '100'
    },
    lensTypeVariant: {
        name: 'lens type variant',
        id: '150'
    },
    prescription: {
        name: 'prescription',
        id: '200'
    },
    prescriptionForm: {
        name: 'prescription',
        id: '210'
    },
    prescriptionUpload: {
        name: 'prescription',
        id: '210'
    },
    prescriptionValidation: {
        name: 'prescription validation',
        id: '250'
    },
    lensPackage: {
        name: 'lens package',
        id: '300'
    },
    lensAddOn: {
        name: 'add-ons',
        id: '400'
    },
    lensAddOnVariant: {
        name: 'add-ons-variant',
        id: '450'
    }
};
/**
 * SHARED SELECTORS
 * Most data layer selectors will use these
 */ const getStepData = createSelector(configuratorIdProp, stateProp, (id, s)=>{
    const currentStep = Selectors.getCurrentStep(s, {
        id
    });
    return currentStep ? stepMapping[currentStep.name] : undefined;
});
const getPrice = createSelector(getBundlePrice, (bundlePrice)=>{
    if (!bundlePrice) {
        console.warn(`Need bundle price to log data layer event. Received "${JSON.stringify(bundlePrice)}"`);
        return;
    }
    const { discountedPrice , originalPrice  } = bundlePrice;
    /**
     * Both "discountedPrice" and "originalPrice" are in cents, as per usual. There is no e.g. "centAmount" property
     * to check here anymore.
     */ return discountedPrice ? (discountedPrice / 100).toFixed(2) : (originalPrice / 100).toFixed(2);
});
const formatPrescriptionValue = (value)=>typeof value === 'number' && !isNaN(value) ? value.toFixed(2) : 'n/a'
;
/**
 * Creates a reducer function that converts PrescriptionValue to a data-layer PrescriptionValue
 */ const reduceParameter = (values)=>(info, { name  })=>swcHelpers.objectSpread({}, info, {
            [name]: formatPrescriptionValue(values[name])
        })
;
const getLensParameters = createSelector(getPrescription, (pre)=>{
    if (!statusSuccess(pre)) {
        return;
    }
    const { parameters , left , right  } = pre.result;
    return {
        OS: parameters.reduce(reduceParameter(left), {}),
        OD: parameters.reduce(reduceParameter(right), {})
    };
});
const getSelectedLensNames = createSelector(getLensTypes, getSelectedLensType, (s)=>s
, (lensTypes, selectedLensType, state)=>{
    if (!lensTypes || !selectedLensType) {
        return;
    }
    const lensTypeProduct = lookupParentLensType(state, {
        lensTypeSku: selectedLensType.sku
    });
    if (!lensTypeProduct) {
        return;
    }
    const lensTypeInfo = convertToLensTypeInfo(lensTypeProduct, selectedLensType.sku, 'en');
    return {
        productName: lensTypeInfo.title,
        variantName: getLensProductVariantName(lensTypeInfo)
    };
});
const getSelectedPackageName = createSelector(getPackages, getSelectedPackage, (packages, selectedPackage)=>{
    if (!packages || !selectedPackage) {
        return;
    }
    const packageProductData = packages[selectedPackage.sku];
    if (!packageProductData) {
        return;
    }
    return getLocalizedValue(packageProductData.name, 'en');
});
const getSelectedAddOnName = createSelector(getSelectedAddOn, (s)=>s
, (selectedAddOn, state)=>{
    if (!selectedAddOn) {
        return 'no add-on';
    }
    const addOnProduct = lookupParentAddOn(state, {
        addOnSku: selectedAddOn.sku
    });
    if (!addOnProduct) {
        return;
    }
    const addOnInfo = convertToLensAddOnInfo(addOnProduct, selectedAddOn.sku, 'en');
    return getBundleProductTitle(addOnInfo);
});
const getProductConfig = createSelector(getSelectedLensNames, getSelectedPackageName, getSelectedAddOnName, getPrice, getLensParameters, hasVisitedStepAddOn, (lensNames, packageName, addOnName, price, lens, hasVisitedStepAddOn)=>{
    return swcHelpers.objectSpread({}, (lensNames === null || lensNames === void 0 ? void 0 : lensNames.productName) ? {
        lensType: lensNames.productName.toLowerCase()
    } : {}, (lensNames === null || lensNames === void 0 ? void 0 : lensNames.variantName) ? {
        lensVariant: lensNames.variantName.toLowerCase()
    } : {}, packageName ? {
        lensPackage: packageName.toLowerCase()
    } : {}, hasVisitedStepAddOn && addOnName ? {
        'add-ons': addOnName.toLowerCase()
    } : {}, {
        price: price !== null && price !== void 0 ? price : '0'
    }, lens ? {
        lens
    } : {});
});
const getConfigFlow = createSelector(getStepData, getPrescriptionType, hasVisitedStepPrescription, (stepData, prescriptionType, hasVisitedStepPrescription)=>{
    /**
     * Should only occur on programmer error. We should always have this data available.
     */ if (!stepData) {
        return undefined;
    }
    const rxInputMethodMapping = {
        NewPrescription: 'manual',
        EmailPrescription: 'email',
        ExistingCustomer: 'customer',
        PrescriptionFile: 'upload',
        NewPrescriptionAndUpload: 'manual',
        'book-appointment': 'book-appointment'
    };
    if (CommerceToolsApi.isPrescriptionFile(prescriptionType) || isNewPrescriptionAndUpload(prescriptionType)) {
        /**
       * Note: there isn't yet a data layer implementation plan for the PrescriptionFile option. For that reason we're
       * not logging anything specific for that option to the data layer.
       */ return {
            version: '1.0',
            step: stepData
        };
    }
    return swcHelpers.objectSpread({
        version: '1.0',
        step: stepData
    }, hasVisitedStepPrescription && isDefined(prescriptionType) ? {
        RxInputMethod: rxInputMethodMapping[prescriptionType]
    } : {});
});
const getUser = createSelector(getPrescriptionType, (prescriptionType)=>{
    if (CommerceToolsApi.isPrescriptionFile(prescriptionType) || isNewPrescriptionAndUpload(prescriptionType)) {
        /**
       * Note: there isn't yet a data layer implementation plan for the PrescriptionFile option. For that reason we're
       * not logging anything specific for that option to the data layer.
       */ return;
    }
    return {
        custStatus: CommerceToolsApi.isExistingCustomer(prescriptionType) ? 'customer' : 'prob noncustomer'
    };
});

export { getConfigFlow, getLensParameters, getPrice, getProductConfig, getSelectedAddOnName, getSelectedLensNames, getSelectedPackageName, getStepData, getUser };
//# sourceMappingURL=selectors.internal.js.map
